import type { InputBaseComponentProps } from '@mui/material/InputBase';
import MuiTextField, { type TextFieldProps } from '@mui/material/TextField';
import { type ChangeEvent, forwardRef, type JSX, useCallback } from 'react';
import { type Control, type FieldPath, type FieldValues, useController } from 'react-hook-form';
import { NumericFormat, type NumericFormatProps } from 'react-number-format';

export type MuiTextFieldProps = TextFieldProps & { 'data-test'?: string };

export type RHFNumberFieldProps<T extends FieldValues> = Omit<
  MuiTextFieldProps,
  'name' | 'value' | 'onChange' | 'type'
> & {
  name: FieldPath<T>;
  control?: Control<T>;
  rules?: Record<string, unknown>;
  min?: number;
  allowDecimals?: boolean;
  decimalScale?: number;
  thousandSeparator?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

type NumericFormatCustomProps = Omit<InputBaseComponentProps, 'inputProps'> & {
  inputProps?: NumericFormatProps & { name?: string; 'data-test'?: string };
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  'data-test'?: string;
};

const NumericFormatCustom = forwardRef<HTMLInputElement, NumericFormatCustomProps>(
  ({ onChange, 'data-test': dataTest, inputProps, ...other }, ref) => {
    return (
      <NumericFormat
        {...inputProps}
        {...other}
        getInputRef={ref}
        data-test={dataTest ? `${dataTest}-input` : undefined}
        onValueChange={values => {
          onChange({
            target: {
              name: inputProps?.name,
              value: values.value,
            },
          } as ChangeEvent<HTMLInputElement>);
        }}
        valueIsNumericString
      />
    );
  }
);

NumericFormatCustom.displayName = 'NumericFormatCustom';

export const RHFNumberField = <T extends FieldValues>({
  name,
  control,
  rules,
  min,
  allowDecimals = true,
  decimalScale,
  thousandSeparator = false,
  onChange,
  slotProps,
  ...rest
}: RHFNumberFieldProps<T>): JSX.Element => {
  const {
    field: { onChange: fieldOnChange, ...restFieldProps },
    fieldState: { invalid, error },
    formState: { isSubmitting },
  } = useController({
    control,
    name,
    rules,
  });

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange ? onChange(e) : fieldOnChange(e);
    },
    [fieldOnChange, onChange]
  );

  return (
    <MuiTextField
      variant="standard"
      slotProps={{
        htmlInput: {
          allowNegative: min === undefined ? true : min < 0,
          decimalScale: allowDecimals ? decimalScale : 0,
          thousandSeparator,
          name,
          'data-test': rest['data-test'],
        },
        input: {
          disableUnderline: true,
          inputComponent: NumericFormatCustom,
        },
        inputLabel: { disableAnimation: true, focused: false, shrink: true },
        ...slotProps,
      }}
      {...restFieldProps}
      {...rest}
      disabled={isSubmitting || rest.disabled}
      onChange={onInputChange}
      error={invalid || rest.error}
      helperText={error?.message ?? rest.helperText}
    />
  );
};
